
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChecklistWithTaches } from '@/hooks/useNouvellesChecklists';
import { Sparkles, Ship, Anchor, Snowflake, List, RotateCcw, Trash2, Edit } from 'lucide-react';

interface TableauProgressionProps {
  checklists: ChecklistWithTaches[];
  onReinitialiser: (checklistId: string) => void;
  onSupprimer: (checklistId: string) => void;
  onModifier: (checklist: ChecklistWithTaches) => void;
}

const TableauProgression = ({ checklists, onReinitialiser, onSupprimer, onModifier }: TableauProgressionProps) => {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Nettoyage':
        return <Sparkles className="h-4 w-4 text-purple-600" />;
      case 'Départ':
        return <Ship className="h-4 w-4 text-blue-600" />;
      case 'Arrivée':
        return <Anchor className="h-4 w-4 text-green-600" />;
      case 'Hivernage':
        return <Snowflake className="h-4 w-4 text-cyan-600" />;
      default:
        return <List className="h-4 w-4 text-gray-600" />;
    }
  };

  const calculerPourcentage = (taches: any[]) => {
    if (taches.length === 0) return 0;
    const tachesTerminees = taches.filter(t => t.etat).length;
    return Math.round((tachesTerminees / taches.length) * 100);
  };

  const getBadgeVariant = (pourcentage: number) => {
    if (pourcentage >= 50) return 'default';
    if (pourcentage >= 30) return 'secondary';
    return 'destructive';
  };

  if (checklists.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Progression des Checklists</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4 text-4xl">📋</div>
            <p className="text-muted-foreground">Aucune checklist disponible</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Progression des Checklists</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {checklists.map((checklist) => {
            const pourcentage = calculerPourcentage(checklist.taches);
            
            return (
              <div key={checklist.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3 flex-1">
                  {getTypeIcon(checklist.type)}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{checklist.nom}</h4>
                      <Badge variant={getBadgeVariant(pourcentage)}>
                        {pourcentage}%
                      </Badge>
                    </div>
                    <Progress 
                      value={pourcentage} 
                      className="h-2"
                    />
                    <div className="text-xs text-muted-foreground mt-1">
                      {checklist.taches.filter(t => t.etat).length} / {checklist.taches.length} tâches terminées
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-4">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onModifier(checklist)}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    title="Modifier"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onReinitialiser(checklist.id)}
                    className="text-nautic hover:text-nautic-light hover:bg-nautic/5"
                    title="Réinitialiser"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onSupprimer(checklist.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default TableauProgression;
